/**
 * (c) 2023, Center for Computational Thinking and Design at Aarhus University and contributors
 * Modifications (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { RiDeleteBin2Line } from "react-icons/ri";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router";
import { useBufferedData } from "../buffered-data-hooks";
import { ButtonWithLoading } from "../components/ButtonWithLoading";
import DefaultPageLayout, {
  ProjectToolbarItems,
} from "../components/DefaultPageLayout";
import IncompatibleEditorDevice from "../components/IncompatibleEditorDevice";
import LiveGraphPanel from "../components/LiveGraphPanel";
import TestingModelTable from "../components/TestingModelTable";
import { useDataConnected } from "../data-connection-flow";
import { useProject } from "../hooks/project-hooks";
import {
  ButtonGroup,
  Flex,
  Icon,
  MenuItem,
  MenuList,
  MenuTrigger,
  MoreMenuButton,
} from "@microbit/ui";
import { useBoardVersion } from "../hooks/use-board-version";
import { usePrevious } from "../hooks/use-previous";
import { keyboardShortcuts, useShortcut } from "../keyboard-shortcut-hooks";
import { projectSessionStorage } from "../session-storage";
import PageActionBar from "../components/PageActionBar";
import { useStore } from "../store";
import { tourElClassname } from "../tours";
import {
  createDataSamplesPageUrl,
  createHomePageUrl,
  TestingModelPageHistoryState,
} from "../urls";

const TestingModelPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const model = useStore((s) => s.model);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const startPredicting = useStore((s) => s.startPredicting);
  const stopPredicting = useStore((s) => s.stopPredicting);
  const bufferedData = useBufferedData();
  const intl = useIntl();

  const navigateToDataSamples = useCallback(() => {
    void navigate(createDataSamplesPageUrl());
  }, [navigate]);

  useEffect(() => {
    if (!projectSessionStorage.getProjectId()) {
      void navigate(createHomePageUrl());
      return;
    }
    if (!model) {
      return navigateToDataSamples();
    }
    startPredicting(bufferedData);

    return () => {
      stopPredicting();
    };
  }, [
    bufferedData,
    model,
    navigate,
    navigateToDataSamples,
    startPredicting,
    stopPredicting,
  ]);

  const tourStart = useStore((s) => s.tourStart);
  const setHasMoved = useStore((s) => s.setHasMoved);
  const isConnected = useDataConnected();
  const wasConnected = usePrevious(isConnected);
  useEffect(() => {
    if (isConnected) {
      void tourStart(
        { name: "TrainModel", delayedUntilConnection: wasConnected === false },
        false
      );
      setHasMoved(true);
    }
  }, [isConnected, setHasMoved, tourStart, wasConnected]);

  const { openEditor, resetProject, projectEdited } = useProject();
  const boardVersion = useBoardVersion();
  const incompatibleEditorDeviceDialogOnOpen = useStore(
    (s) => s.incompatibleEditorDeviceDialogOnOpen
  );
  const isIncompatibleEditorDeviceDialogOpen = useStore(
    (s) => s.isIncompatibleEditorDeviceDialogOpen
  );
  const closeDialog = useStore((s) => s.closeDialog);
  const maybeOpenEditor = useCallback(async () => {
    // Open editor if device is not a V1, otherwise show warning dialog.
    if (boardVersion === "V1") {
      return incompatibleEditorDeviceDialogOnOpen();
    }
    const focusVisible =
      editButtonRef.current?.matches(":focus-visible") ?? false;
    setEditorLoading(true);
    await openEditor(focusVisible);
    setEditorLoading(false);
  }, [boardVersion, incompatibleEditorDeviceDialogOnOpen, openEditor]);
  const [editorLoading, setEditorLoading] = useState(false);
  const continueToEditor = useCallback(async () => {
    setEditorLoading(true);
    await openEditor();
    closeDialog();
    setEditorLoading(false);
  }, [closeDialog, openEditor]);
  useShortcut(keyboardShortcuts.editInMakeCode, maybeOpenEditor);
  useEffect(() => {
    const state = location.state as TestingModelPageHistoryState | undefined;
    if (state?.fromEditor) {
      editButtonRef.current?.focus({
        focusVisible: state.focusVisible ?? false,
      });
      // Clear the state so a page refresh doesn't re-focus the button.
      void navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  return model ? (
    <DefaultPageLayout
      titleId="testing-model-title"
      showPageTitle
      showProjectName
      toolbarItemsRight={<ProjectToolbarItems />}
      backUrl={createDataSamplesPageUrl()}
      backLabelId="back-to-data-samples-action"
      bottomContent={
        <>
          <PageActionBar
            aria-label={intl.formatMessage({
              id: "testing-model-actions-region",
            })}
            justify="right"
          >
            <ButtonGroup isAttached>
              <ButtonWithLoading
                ref={editButtonRef}
                variant="primary"
                onClick={maybeOpenEditor}
                className={tourElClassname.editInMakeCodeButton}
                isLoading={
                  editorLoading && !isIncompatibleEditorDeviceDialogOpen
                }
              >
                <FormattedMessage id="edit-in-makecode-action" />
              </ButtonWithLoading>
              <MenuTrigger>
                <MoreMenuButton
                  variant="primary"
                  aria-label={intl.formatMessage({
                    id: "more-edit-in-makecode-options",
                  })}
                />
                <MenuList>
                  <MenuItem
                    icon={<Icon as={RiDeleteBin2Line} />}
                    onAction={resetProject}
                    isDisabled={!projectEdited}
                    textValue={intl.formatMessage({
                      id: "reset-to-default-action",
                    })}
                  >
                    <FormattedMessage id="reset-to-default-action" />
                  </MenuItem>
                </MenuList>
              </MenuTrigger>
            </ButtonGroup>
          </PageActionBar>
          <LiveGraphPanel
            showPredictedAction
            disconnectedTextId="connect-to-test-model"
          />
        </>
      }
    >
      <IncompatibleEditorDevice
        isOpen={isIncompatibleEditorDeviceDialogOpen}
        onClose={closeDialog}
        onNext={continueToEditor}
        stage="openEditor"
        onNextLoading={editorLoading}
      />
      <Flex as="main" flexGrow={1} flexDir="column">
        <TestingModelTable />
      </Flex>
    </DefaultPageLayout>
  ) : (
    <></>
  );
};

export default TestingModelPage;
